import { Button } from '@cargoflow/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@cargoflow/ui/components/card'
import { Checkbox } from '@cargoflow/ui/components/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@cargoflow/ui/components/dialog'
import { Input } from '@cargoflow/ui/components/input'
import { Label } from '@cargoflow/ui/components/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@cargoflow/ui/components/table'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, Loader2, Mail, Send } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { match } from 'ts-pattern'
import { api } from '@/lib/api'

export const Route = createFileRoute('/_authenticated/campaigns/')({
  component: CampaignsPage,
})

const PAGE_SIZE = 20
const RECIPIENT_CAP = 50
const DEFAULT_SUBJECT_RO = 'Nu rata cele mai noi curse pe CargoFlow'

function CampaignsPage() {
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [subject, setSubject] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)

  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      status: 'approved' as const,
      search: searchQuery || undefined,
      sort: 'created_at' as const,
      order: 'desc' as const,
    }),
    [page, searchQuery],
  )

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['admin', 'campaigns', 'companies', filters],
    queryFn: async () => {
      const { data, error } = await api.admin.companies.get({ query: filters })
      if (error) throw new Error(typeof error.value === 'string' ? error.value : 'Failed to load')
      return data
    },
    placeholderData: keepPreviousData,
  })

  const sendMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.admin.campaigns.reengagement.post({
        company_ids: Array.from(selectedIds),
        subject: subject.trim() || undefined,
      })
      if (error) throw new Error(typeof error.value === 'string' ? error.value : 'Send failed')
      return data
    },
    onSuccess: (result) => {
      if (result.failed === 0) {
        toast.success(`Sent to ${result.sent} compan${result.sent === 1 ? 'y' : 'ies'}`)
      } else {
        toast.warning(`Sent ${result.sent} · ${result.failed} failed (check audit log for details)`)
      }
      setSelectedIds(new Set())
      setSubject('')
      setConfirmOpen(false)
    },
    onError: (err: Error) => {
      toast.error(err.message)
    },
  })

  const remainingSlots = RECIPIENT_CAP - selectedIds.size

  function toggle(id: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) {
        if (next.size >= RECIPIENT_CAP) return prev
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }

  function selectAllOnPage(checked: boolean) {
    if (!data) return
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) {
        for (const company of data.data) {
          if (next.size >= RECIPIENT_CAP) break
          next.add(company.id)
        }
      } else {
        for (const company of data.data) {
          next.delete(company.id)
        }
      }
      return next
    })
  }

  const allOnPageSelected =
    !!data && data.data.length > 0 && data.data.every((c) => selectedIds.has(c.id))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Campaigns</h1>
          <p className="text-muted-foreground text-sm">
            Send a re-engagement email to approved carrier companies. Cap of {RECIPIENT_CAP}{' '}
            recipients per send.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4" /> Re-engagement email
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="subject">Subject (optional)</Label>
            <Input
              id="subject"
              placeholder={DEFAULT_SUBJECT_RO}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
            <p className="text-muted-foreground text-xs">
              Leave empty to use the localized default. Body is fixed: a one-line nudge plus a
              &quot;View loads&quot; button.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-3">
        <Input
          placeholder="Search name, CUI, or email…"
          className="max-w-sm"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onBlur={() => {
            setSearchQuery(searchInput.trim())
            setPage(1)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearchQuery(searchInput.trim())
              setPage(1)
            }
          }}
        />
        <div className="flex items-center gap-3 text-sm">
          <span className="text-muted-foreground">
            {selectedIds.size} / {RECIPIENT_CAP} selected
          </span>
          <Button
            disabled={selectedIds.size === 0 || sendMutation.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            <Send className="h-4 w-4" /> Send
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allOnPageSelected}
                  onCheckedChange={(checked) => selectAllOnPage(checked === true)}
                  aria-label="Select all on this page"
                />
              </TableHead>
              <TableHead>Company</TableHead>
              <TableHead>CUI</TableHead>
              <TableHead>Contact email</TableHead>
              <TableHead>Country</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  <Loader2 className="inline h-4 w-4 animate-spin" />
                </TableCell>
              </TableRow>
            )}
            {error && (
              <TableRow>
                <TableCell colSpan={5} className="text-destructive h-24 text-center">
                  {(error as Error).message}
                </TableCell>
              </TableRow>
            )}
            {data?.data.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground h-24 text-center">
                  No approved companies found
                </TableCell>
              </TableRow>
            )}
            {data?.data.map((company) => {
              const checked = selectedIds.has(company.id)
              const disabled = !checked && remainingSlots <= 0
              return (
                <TableRow key={company.id} data-state={checked ? 'selected' : undefined}>
                  <TableCell>
                    <Checkbox
                      checked={checked}
                      disabled={disabled}
                      onCheckedChange={(value) => toggle(company.id, value === true)}
                      aria-label={`Select ${company.name}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{company.name}</TableCell>
                  <TableCell className="font-mono text-xs">{company.cui}</TableCell>
                  <TableCell className="text-sm">{company.contact_email}</TableCell>
                  <TableCell className="text-sm">{company.address_country ?? '—'}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            Page {data.page} of {data.total_pages} · {data.total} total
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={data.page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={data.page >= data.total_pages || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send re-engagement email?</DialogTitle>
            <DialogDescription>
              {selectedIds.size} compan{selectedIds.size === 1 ? 'y' : 'ies'} will receive the
              &quot;don&apos;t miss the latest loads&quot; email at their verified contact address.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={sendMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={() => sendMutation.mutate()} disabled={sendMutation.isPending}>
              {match(sendMutation.isPending)
                .with(true, () => (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ))
                .with(false, () => (
                  <>
                    <Send className="h-4 w-4" /> Send now
                  </>
                ))
                .exhaustive()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
